import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Popconfirm, Row } from 'antd';
import i18n from 'i18next';

import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as PencilIcon } from '../../../../assets/icons/pencil-white.svg';
import { setPageBlocker } from '../../../../redux/common/CommonActions';
import useShipmentEditPermission from '../../../../redux/hooks/useShipmentEditPermission';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import IconButton from '../../../components/Buttons/IconButton';
import SvgConverter from '../../../components/Common/SvgConverter';

const SaveCancelButtons = ({
  lightMode = undefined,
  editMode = true,
  shipmentEditStatus = false,
  onCancel = () => {},
  handleRemove = () => {},
  setEditMode,
  onSave = false,
  hideDeleteButton = false,
  isNew = false,
  cancelButtonText = i18n.t('translation:default.buttons.cancel'),
  saveButtonText = i18n.t('translation:default.buttons.add'),
  idSuffix = '',
  isModified = false,
  isDeleteLoading = false,
  hideEditButton = false,
  saveButtonType = 'submit',
  disabled = false,
}) => {
  const hasEditPermission = useShipmentEditPermission();
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const dispatch = useDispatch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    if (isModified !== null) {
      dispatch(setPageBlocker(isModified));
    }
  }, [isModified]);

  return (
    <>
      {editMode ? (
        <Row gutter={16} className="flex-nowrap">
          <Col>
            <Button
              disabled={disabled}
              type="primary"
              variant="success"
              buttonType={saveButtonType}
              lightMode={lightMode}
              {...(onSave ? { onClick: onSave } : {})}
              id={`add-save-${idSuffix}`}
              dataTestId={`add-save-${idSuffix}`}
            >
              {isNew ? saveButtonText : t('translation:default.buttons.update')}
            </Button>
          </Col>
          <Col>
            {isModified ? (
              <Popconfirm
                placement="topRight"
                title={t('translation:default.fields.changesWillBeLost')}
                onConfirm={onCancel}
                okText={t('translation:default.fields.modal.okText')}
                cancelText={t('translation:default.options.no')}
                classNames={{ root: 'delete-popup-container' }}
                getPopupContainer={(trigger) => trigger.parentElement}
              >
                <Button
                  type="default"
                  variant="danger"
                  buttonType="button"
                  lightMode={lightMode}
                  id={`cancel-${idSuffix}`}
                  dataTestId={`cancel-${idSuffix}`}
                >
                  {cancelButtonText}
                </Button>
              </Popconfirm>
            ) : (
              <Button
                type="default"
                variant="danger"
                buttonType="button"
                lightMode={lightMode}
                dataTestId={`cancel-${idSuffix}`}
                id={`cancel-${idSuffix}`}
                onClick={onCancel}
              >
                {cancelButtonText}
              </Button>
            )}
          </Col>
        </Row>
      ) : (
        <Row className="flex-nowrap">
          {!hideEditButton ? (
            <Col className={`${!hideDeleteButton ? 'mr-16' : ''} lh-0`}>
              <IconButton
                onClick={() => setEditMode?.(true)}
                disabled={shipmentEditStatus || !hasEditPermission}
                dataTestId={`edit-${idSuffix}`}
                id={`edit-${idSuffix}`}
              >
                <SvgConverter disabled={shipmentEditStatus || !hasEditPermission} Icon={PencilIcon} color="header" />
              </IconButton>
            </Col>
          ) : null}

          {!hideDeleteButton && (
            <Col className="lh-0">
              <Popconfirm
                placement="topRight"
                title={t('translation:default.popConfirm.title')}
                onConfirm={handleRemove}
                okText={t('translation:default.buttons.delete')}
                cancelText={t('translation:default.options.no')}
                classNames={{ root: 'delete-popup-container' }}
                okType="danger"
                getPopupContainer={(trigger) => trigger.parentElement}
              >
                <IconButton
                  disabled={shipmentEditStatus || !hasEditPermission}
                  id={`delete-${idSuffix}`}
                  dataTestId={`delete-${idSuffix}`}
                  loading={isDeleteLoading}
                >
                  <SvgConverter
                    disabled={shipmentEditStatus || !hasEditPermission}
                    Icon={DeleteIcon}
                    color="iconDanger"
                  />
                </IconButton>
              </Popconfirm>
            </Col>
          )}
        </Row>
      )}
    </>
  );
};

export default SaveCancelButtons;
